import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  CalendarProps,
  Checkbox,
  cn,
  IconButton,
  Label,
  Modal,
  ModalActions,
  ModalContent,
  ModalTitleInverse,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextInput,
} from '@c2fo/liquidity';
import { TrashCanIcon } from '@c2fo/liquidity/icons';
import DatePicker from '@/enterprise/components/DatePicker';
import { PlayableInvoiceExclusionsSearch } from '@/enterprise/data/usePlayableInvoiceExclusions';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import { useReporting } from '@/reporting';
import { dateAsIsoString } from '@/utils/dateAsIsoString';
import useLocaleFormat from '@/utils/useLocaleFormat';

export interface CriteriaPayload {
  takerMarkets: TakerMarket[];
  search: PlayableInvoiceExclusionsSearch;
  reset: boolean;
  operator: 'AND' | 'OR';
}

interface EditInvoiceCriteriaModalProps {
  onClose: () => void;
  onApply: (criteria: CriteriaPayload) => Promise<void>;
  open: boolean;
  referrer: 'customer-select' | 'division-details';
  takerMarkets: TakerMarket[];
}

type FilterProperty = 'daysPaidEarly' | 'invoiceAmount' | 'invoiceDueDate';
type FilterOperator = 'gte' | 'lte' | 'between' | 'before' | 'after';

interface Filter {
  property: FilterProperty;
  operator: FilterOperator;
  value: string;
  value2?: string;
}

const FILTER_CONFIG = {
  daysPaidEarly: {
    label: 'core.daysPaidEarly',
    inputType: 'number',
    operators: [
      { value: 'gte', label: 'recurringRules.invoiceAmount.optionGreater.label' },
      { value: 'lte', label: 'recurringRules.invoiceAmount.optionLess.label' },
      { value: 'between', label: 'core.isBetween' },
    ],
    defaultOperator: 'gte',
  },
  invoiceAmount: {
    label: 'core.invoiceAmount',
    inputType: 'number',
    operators: [
      { value: 'gte', label: 'recurringRules.invoiceAmount.optionGreater.label' },
      { value: 'lte', label: 'recurringRules.invoiceAmount.optionLess.label' },
      { value: 'between', label: 'core.isBetween' },
    ],
    defaultOperator: 'gte',
  },
  invoiceDueDate: {
    label: 'recurringRules.dueDate.category.label',
    inputType: 'date',
    operators: [
      { value: 'before', label: 'recurringRules.dueDate.optionLess.label' },
      { value: 'after', label: 'recurringRules.dueDate.optionGreater.label' },
      { value: 'between', label: 'core.isBetween' },
    ],
    defaultOperator: 'before',
  },
} satisfies Record<
  FilterProperty,
  {
    label: string;
    inputType: string;
    operators: Array<{ value: FilterOperator; label: string }>;
    defaultOperator: FilterOperator;
  }
>;

const propertyOptions: Array<{ value: FilterProperty; label: string }> = [
  { value: 'daysPaidEarly', label: FILTER_CONFIG.daysPaidEarly.label },
  { value: 'invoiceAmount', label: FILTER_CONFIG.invoiceAmount.label },
  { value: 'invoiceDueDate', label: FILTER_CONFIG.invoiceDueDate.label },
];

const logicalOperatorOptions = [
  { value: 'AND', label: 'core.and' },
  { value: 'OR', label: 'core.or' },
];

const isValidFilter = (filter: Filter): boolean => {
  const hasBasicValues = filter.property && filter.operator && filter.value.trim();
  const hasBetweenValues = filter.operator === 'between' ? filter.value2?.trim() : true;

  // require a property, operator, and non-empty primary value (and second value for 'between')
  if (!hasBasicValues || !hasBetweenValues) {
    return false;
  }

  // the API rejects floats for daysPaidEarly — enforce integers here before submission
  if (filter.property === 'daysPaidEarly') {
    const val1 = Number(filter.value);
    if (!Number.isInteger(val1) || val1 <= 0) {
      return false;
    }

    // 'between' operator has a second value that needs the same check
    if (filter.operator === 'between' && filter.value2) {
      const val2 = Number(filter.value2);
      if (!Number.isInteger(val2) || val2 <= 0) {
        return false;
      }
    }
  }

  return true;
};

interface NumericFilter {
  lt?: number;
  lte?: number;
  gt?: number;
  gte?: number;
}

interface DateFilter {
  from?: string;
  to?: string;
  before?: string;
  after?: string;
}

const isDateFilter = (filter: Filter): filter is Filter & { property: 'invoiceDueDate' } => {
  return filter.property === 'invoiceDueDate';
};

const isNumericFilter = (filter: Filter): filter is Filter & { property: 'daysPaidEarly' | 'invoiceAmount' } => {
  return filter.property === 'daysPaidEarly' || filter.property === 'invoiceAmount';
};

const buildDateFilter = (filter: Filter & { property: 'invoiceDueDate' }): DateFilter => {
  const filterObj: DateFilter = {};

  if (filter.operator === 'before') {
    filterObj.to = filter.value;
  } else if (filter.operator === 'after') {
    filterObj.from = filter.value;
  } else if (filter.operator === 'between') {
    filterObj.from = filter.value;
    filterObj.to = filter.value2;
  }

  return filterObj;
};

const buildNumericFilter = (filter: Filter & { property: 'daysPaidEarly' | 'invoiceAmount' }): NumericFilter => {
  const filterObj: NumericFilter = {};

  if (filter.operator === 'gte') {
    filterObj.gte = Number(filter.value);
  } else if (filter.operator === 'lte') {
    filterObj.lte = Number(filter.value);
  } else if (filter.operator === 'between') {
    filterObj.gte = Number(filter.value);
    filterObj.lte = Number(filter.value2);
  }

  return filterObj;
};

interface FilterDatePickerProps {
  property: FilterProperty;
  field: 'value' | 'value2';
  value?: string;
  disabledOptions?: CalendarProps<'single'>['disabled'];
  defaultMonth?: Date;
  onSelect: (property: FilterProperty, field: 'value' | 'value2', value: string) => void;
  datePickerOpenStates: Record<string, boolean>;
  setDatePickerOpenStates: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  className?: string;
}

const FilterDatePicker = ({
  property,
  field,
  value,
  disabledOptions,
  defaultMonth,
  onSelect,
  datePickerOpenStates,
  setDatePickerOpenStates,
  className,
}: FilterDatePickerProps) => {
  const { t } = useTranslation();
  const stateKey = `${property}-${field}`;

  return (
    <DatePicker
      open={datePickerOpenStates[stateKey] || false}
      onOpenChange={(open) => setDatePickerOpenStates((prev) => ({ ...prev, [stateKey]: open }))}
      onSelect={(e) => {
        const dateString = dateAsIsoString(e);
        onSelect(property, field, dateString);
        setDatePickerOpenStates((prev) => ({ ...prev, [stateKey]: false }));
      }}
      selected={value || null}
      disableDefaultSelection={!value}
      defaultMonth={defaultMonth}
      triggerClassName="w-full"
      trigger={
        <div
          aria-label={value || t('core.selectDate')}
          className={cn(
            'flex w-full items-center rounded border border-stroke bg-white px-4 py-3 text-text-primary',
            {
              'text-text-secondary': !value,
            },
            className
          )}
          role="button"
        >
          {value || t('core.selectDate')}
        </div>
      }
      disabledOptions={disabledOptions}
    />
  );
};

const EditInvoiceCriteriaModal = ({
  takerMarkets,
  open,
  onClose,
  onApply,
  referrer,
}: EditInvoiceCriteriaModalProps) => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const [resetInvoicesBeforeApplying, setResetInvoicesBeforeApplying] = useState(true);
  const [logicalOperator, setLogicalOperator] = useState<'AND' | 'OR'>('OR');
  const [datePickerOpenStates, setDatePickerOpenStates] = useState<Record<string, boolean>>({});
  const [filters, setFilters] = useState<Filter[]>([
    {
      property: 'daysPaidEarly',
      operator: 'gte',
      value: '',
    },
  ]);
  // get invoice rules for all taker markets
  const allRecurringRules = useMemo(
    () => getRecurringRulesForTakerMarkets(takerMarkets),
    [takerMarkets, getRecurringRulesForTakerMarkets]
  );
  const takerMarketsWithRules = useMemo(() => {
    return new Set(
      allRecurringRules.map((rule) => `${rule.makerOrganizationUuid}-${rule.takerUuid}-${rule.marketUuid}`)
    );
  }, [allRecurringRules]);
  // only update taker markets when:
  // referrer is customer select in offer builder and do not have active offers or invoice rules
  // referrer is eligible invoice table and do not have invoice rules (active offers are allowed)
  const takerMarketsToUpdate = useMemo(
    () =>
      takerMarkets.filter((takerMarket) => {
        if (referrer === 'customer-select' && takerMarket.offerConfig.isEnabled) {
          return false;
        }

        const key = `${takerMarket.makerOrganizationUuid}-${takerMarket.takerDivisionUuid}-${takerMarket.marketUuid}`;
        return !takerMarketsWithRules.has(key);
      }),
    [referrer, takerMarkets, takerMarketsWithRules]
  );

  const getOperatorOptions = (property: FilterProperty) => {
    return FILTER_CONFIG[property].operators;
  };

  const getInputType = (property: FilterProperty) => {
    return FILTER_CONFIG[property].inputType;
  };

  const addFilter = () => {
    if (filters.length >= 3) {
      return;
    }

    const usedProperties = filters.map((f) => f.property);
    const availableProperty = propertyOptions.find((option) => !usedProperties.includes(option.value))?.value;

    if (!availableProperty) {
      return;
    }

    const config = FILTER_CONFIG[availableProperty];
    const newFilter: Filter = {
      property: availableProperty,
      operator: config.defaultOperator,
      value: '',
    };

    setFilters([...filters, newFilter]);
  };

  const updateFilter = (property: FilterProperty, field: keyof Filter, value: string) => {
    setFilters(
      filters.map((filter) => {
        if (filter.property === property) {
          const updatedFilter = { ...filter, [field]: value };

          if (
            field === 'property' &&
            (value === 'daysPaidEarly' || value === 'invoiceAmount' || value === 'invoiceDueDate')
          ) {
            const newProperty: FilterProperty = value;
            const isPropertyUsed = filters.some((f) => f.property !== property && f.property === newProperty);
            if (isPropertyUsed) {
              return filter;
            }

            const config = FILTER_CONFIG[newProperty];
            updatedFilter.operator = config.defaultOperator;
            updatedFilter.value = '';
            updatedFilter.value2 = '';
          }

          if (field === 'operator' && value !== 'between') {
            updatedFilter.value2 = '';
          }

          return updatedFilter;
        }

        return filter;
      })
    );
  };

  const handleApplyFilters = async () => {
    const validFilters = filters.filter(isValidFilter);

    const searchFilters: {
      daysPaidEarly?: Array<{ lt?: number; lte?: number; gt?: number; gte?: number }>;
      amount?: Array<{ lt?: number; lte?: number; gt?: number; gte?: number }>;
      dueDate?: Array<{ from?: string; to?: string; before?: string; after?: string }>;
    } = {};

    validFilters.forEach((filter) => {
      if (isDateFilter(filter)) {
        if (!searchFilters.dueDate) {
          searchFilters.dueDate = [];
        }

        searchFilters.dueDate.push(buildDateFilter(filter));
      } else if (isNumericFilter(filter)) {
        if (filter.property === 'daysPaidEarly') {
          if (!searchFilters.daysPaidEarly) {
            searchFilters.daysPaidEarly = [];
          }

          searchFilters.daysPaidEarly.push(buildNumericFilter(filter));
        } else if (filter.property === 'invoiceAmount') {
          if (!searchFilters.amount) {
            searchFilters.amount = [];
          }

          searchFilters.amount.push(buildNumericFilter(filter));
        }
      }
    });

    takerMarketsToUpdate.forEach((takerMarket) => {
      trackEnterpriseEvent('invoices::criteria-applied', {
        makerOrgUuid: takerMarket.makerOrganizationUuid,
        marketUuid: takerMarket.marketUuid,
        takerUuid: takerMarket.takerDivisionUuid,
        action: 'include',
        criteriaCount: validFilters.length,
        criteriaTypes: validFilters.map((f) => f.property),
        referrer,
      });
    });

    await onApply({
      takerMarkets: takerMarketsToUpdate,
      search: searchFilters,
      reset: resetInvoicesBeforeApplying,
      operator: logicalOperator,
    });

    onClose();
    resetCriteria();
  };

  const hasValidFilters = filters.some(isValidFilter);

  const resetCriteria = () => {
    setResetInvoicesBeforeApplying(true);
    setLogicalOperator('OR');
    setDatePickerOpenStates({});
    setFilters([
      {
        property: 'daysPaidEarly',
        operator: 'gte',
        value: '',
      },
    ]);
  };

  const handleClose = () => {
    resetCriteria();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} className="max-w-[888px]">
      <ModalTitleInverse>{t('invoices.includeByCriteriaTitle')}</ModalTitleInverse>
      <ModalContent className="border-t">
        <div className="space-y-3">
          <div className="pb-3">{t('invoices.includeInvoicesByCriteriaDescription')}</div>
          <div className="space-y-3 rounded-xl bg-canvas p-4">
            {filters.map((filter, index) => (
              <Fragment key={filter.property}>
                {index !== 0 && (
                  <div className={cn({ 'w-24 shrink-0': index !== 0 })}>
                    <>
                      {index === 1 ? (
                        <Select
                          value={logicalOperator}
                          onValueChange={(value) => {
                            if (value === 'AND' || value === 'OR') {
                              setLogicalOperator(value);
                            }
                          }}
                        >
                          <SelectTrigger aria-label="Logical operator">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {logicalOperatorOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {t(option.label)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="text-text-secondary">
                          {logicalOperator === 'AND' ? t('core.and') : t('core.or')}
                        </div>
                      )}
                    </>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="w-full md:w-48">
                    <Select
                      value={filter.property}
                      onValueChange={(value) => updateFilter(filter.property, 'property', value)}
                    >
                      <SelectTrigger aria-label="Filter property">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyOptions.map((option) => {
                          const isUsedByOther = filters.some(
                            (f) => f.property !== filter.property && f.property === option.value
                          );
                          return (
                            <SelectItem key={option.value} value={option.value} disabled={isUsedByOther}>
                              {t(option.label)}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="w-full md:w-64">
                    <Select
                      value={filter.operator}
                      onValueChange={(value) => updateFilter(filter.property, 'operator', value)}
                    >
                      <SelectTrigger aria-label="Filter operator">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getOperatorOptions(filter.property).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.label)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <div className="min-w-0 flex-1">
                      {filter.property === 'invoiceDueDate' ? (
                        <FilterDatePicker
                          className="w-auto md:w-full"
                          property={filter.property}
                          field="value"
                          value={filter.value}
                          disabledOptions={{ before: new Date() }}
                          onSelect={(property, field, value) => updateFilter(property, field, value)}
                          datePickerOpenStates={datePickerOpenStates}
                          setDatePickerOpenStates={setDatePickerOpenStates}
                        />
                      ) : (
                        <TextInput
                          type={getInputType(filter.property)}
                          value={filter.value}
                          onChange={(e) => updateFilter(filter.property, 'value', e.target.value)}
                          name={`${filter.property} filter value`}
                          className="w-auto"
                        />
                      )}
                    </div>
                    {filter.operator === 'between' && (
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="text-sm text-text-secondary">and</span>
                        <div className="min-w-0 flex-1">
                          {filter.property === 'invoiceDueDate' ? (
                            <FilterDatePicker
                              property={filter.property}
                              field="value2"
                              value={filter.value2}
                              defaultMonth={filter.value ? new Date(filter.value) : undefined}
                              disabledOptions={
                                filter.value
                                  ? (date: Date) => {
                                      const firstDate = new Date(filter.value);
                                      const today = new Date();
                                      const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                      const firstDateOnly = new Date(
                                        firstDate.getFullYear(),
                                        firstDate.getMonth(),
                                        firstDate.getDate()
                                      );
                                      const todayOnly = new Date(
                                        today.getFullYear(),
                                        today.getMonth(),
                                        today.getDate()
                                      );

                                      return dateOnly <= firstDateOnly || dateOnly < todayOnly;
                                    }
                                  : { before: new Date() }
                              }
                              onSelect={(property, field, value) => updateFilter(property, field, value)}
                              datePickerOpenStates={datePickerOpenStates}
                              setDatePickerOpenStates={setDatePickerOpenStates}
                            />
                          ) : (
                            <TextInput
                              type={getInputType(filter.property)}
                              value={filter.value2 || ''}
                              onChange={(e) => updateFilter(filter.property, 'value2', e.target.value)}
                              name={`${filter.property} filter second value`}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="ml-auto">
                    <IconButton
                      className="bg-transparent"
                      disabled={filters.length <= 1}
                      icon={TrashCanIcon}
                      inverse
                      name="remove-filter"
                      onClick={() => setFilters(filters.filter((f) => f.property !== filter.property))}
                      size="sm"
                      variant="destructive"
                    />
                  </div>
                </div>
              </Fragment>
            ))}
            <div className="">
              <Button disabled={filters.length >= 3} onClick={addFilter} size="sm" variant="link">
                {t('recurringRules.addCriteria')}
              </Button>
            </div>
          </div>
          <div className="flex items-start gap-3 pt-3">
            <Checkbox
              id="reset-invoices-before-applying"
              checked={resetInvoicesBeforeApplying}
              onChange={() => setResetInvoicesBeforeApplying(!resetInvoicesBeforeApplying)}
            />
            <Label htmlFor="reset-invoices-before-applying" className="-mt-0.5 cursor-pointer">
              <div>{t('invoices.applyCriteriaToAllInvoices')}</div>
              <div className="text-sm font-normal text-text-secondary">
                {t('invoices.applyCriteriaToAllInvoicesDescription')}
              </div>
            </Label>
          </div>
          {takerMarketsToUpdate.length !== takerMarkets.length ? (
            <div className="pt-3">
              <Alert
                description={t('invoices.applyCriteriaToAllInvoicesAlertDescription')}
                full
                title={t('invoices.applyCriteriaToAllInvoicesAlertTitle', {
                  formattedCount: asNumber({ value: takerMarketsToUpdate.length }),
                  formattedTotalCount: asNumber({ value: takerMarkets.length }),
                })}
                type="warning"
                variant="outlined"
              />
            </div>
          ) : null}
        </div>
      </ModalContent>
      <ModalActions>
        <Button onClick={handleClose} variant="secondary">
          {t('core.cancel')}
        </Button>
        <Button disabled={!hasValidFilters} onClick={handleApplyFilters} variant="primary">
          {t('invoices.applyInclusions')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default EditInvoiceCriteriaModal;
